/**
 * useOSMMosques — fetches real mosque data from OpenStreetMap Overpass API
 * near a given lat/lng within a given radius (meters).
 *
 * Returns mosques in the same shape as the D1 feed:
 * { id, name, lat, lng, address, today: null }
 *
 * Caches results in memory per (lat, lng, radius) key to avoid
 * repeat API calls on re-renders.
 */

import { useState, useEffect, useRef } from 'react';

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const DEFAULT_RADIUS = 3000; // 3km

// Simple in-memory cache: key → { data, fetchedAt }
const osmCache = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function buildOverpassQuery(lat, lng, radius) {
  // Query all mosques (place_of_worship + religion=muslim) within radius
  return `
[out:json][timeout:25];
(
  node["amenity"="place_of_worship"]["religion"="muslim"](around:${radius},${lat},${lng});
  way["amenity"="place_of_worship"]["religion"="muslim"](around:${radius},${lat},${lng});
  relation["amenity"="place_of_worship"]["religion"="muslim"](around:${radius},${lat},${lng});
);
out center tags;
`.trim();
}

function parseOSMResult(elements) {
  return elements
    .map((el) => {
      // nodes have lat/lng directly; ways/relations have a center
      const lat = el.lat ?? el.center?.lat;
      const lng = el.lon ?? el.center?.lon;
      if (!lat || !lng) return null;

      const tags = el.tags || {};
      const name =
        tags['name:bn'] ||   // Bengali name preferred
        tags.name ||
        tags['name:en'] ||
        'মসজিদ';

      const address = [
        tags['addr:street'],
        tags['addr:suburb'],
        tags['addr:city'],
      ]
        .filter(Boolean)
        .join(', ') || tags['addr:full'] || '';

      return {
        id: `osm_${el.type}_${el.id}`,
        name,
        lat,
        lng,
        address,
        today: null, // will be merged with D1 feed data
        source: 'osm',
      };
    })
    .filter(Boolean);
}

export function useOSMMosques(userLocation, radius = DEFAULT_RADIUS) {
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!userLocation) return;

    const { lat, lng } = userLocation;

    // Round to ~100m precision for cache key
    const cacheKey = `${lat.toFixed(3)},${lng.toFixed(3)},${radius}`;
    const cached = osmCache[cacheKey];
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      setMosques(cached.data);
      return;
    }

    // Abort previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const query = buildOverpassQuery(lat, lng, radius);

    fetch(OVERPASS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `data=${encodeURIComponent(query)}`,
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Overpass HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const parsed = parseOSMResult(json.elements || []);
        osmCache[cacheKey] = { data: parsed, fetchedAt: Date.now() };
        setMosques(parsed);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return; // ignore cancellations
        console.warn('OSM Overpass fetch failed:', err.message);
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, [userLocation, radius]);

  return { mosques, loading, error };
}
